import { useMemo } from "react";
import { articles } from "../../../mocks/hotNews";
import SectionHeading from "./SectionHeading";

const feedCategories = ["全部", "娱乐吃瓜", "影视名场", "科技热闻", "社会热议", "生活话题", "体坛速递"];

interface NewsFeedProps {
  activeCategory: string;
  onCategoryChange: (c: string) => void;
  query: string;
}

function smallCard(a: (typeof articles)[number]) {
  return (
    <a
      key={a.id}
      href="#crowd"
      className="group flex flex-col gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 sm:flex-row sm:items-center"
    >
      <img
        src={a.cover}
        alt={`${a.category}：${a.title}`}
        title={`${a.title} - 看料门现场直击`}
        className="h-36 w-full shrink-0 rounded-xl object-cover object-center sm:h-24 sm:w-32"
      />
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center gap-2 text-[11px]">
          <span className="rounded-md bg-primary-100 px-2 py-0.5 font-medium text-primary-700">{a.category}</span>
          <span className="text-foreground-500">{a.time}</span>
        </div>
        <h4 className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700 md:text-base">
          {a.title}
        </h4>
        <p className="mt-1.5 hidden text-xs leading-relaxed text-foreground-500 lg:line-clamp-1">{a.summary}</p>
        <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-accent-500"></i>
            {a.heat}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>
            {a.comments.toLocaleString()}
          </span>
        </div>
      </div>
    </a>
  );
}

function bigCard(a: (typeof articles)[number]) {
  return (
    <a
      key={a.id}
      href="#crowd"
      className="group relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-2xl lg:col-span-2 lg:min-h-[560px]"
    >
      <img
        src={a.cover}
        alt={`${a.category}：${a.title}`}
        title={`${a.title} - 看料门现场直击`}
        className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent"></div>
      <div className="relative p-6 md:p-8">
        <div className="mb-3 flex items-center gap-2">
          <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">{a.category}</span>
          <span className="rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-foreground-950">
            头条 · {a.tag}
          </span>
        </div>
        <h3 className="font-heading text-2xl leading-snug text-background-50 md:text-3xl">{a.title}</h3>
        <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-foreground-200 sm:block">{a.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-300">
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            {a.time}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-line text-accent-400"></i>
            {a.heat}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-chat-3-line"></i>
            {a.comments.toLocaleString()} 讨论
          </span>
          <span className="ml-auto hidden items-center gap-1 text-primary-400 group-hover:gap-2 sm:flex">
            前排围观 <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </a>
  );
}

export default function NewsFeed({ activeCategory, onCategoryChange, query }: NewsFeedProps) {
  const filtered = useMemo(() => {
    return articles.filter((a) => {
      const catOk = activeCategory === "全部" || a.category === activeCategory;
      const q = query.trim().toLowerCase();
      const kwOk = !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q);
      return catOk && kwOk;
    });
  }, [activeCategory, query]);

  const featured = filtered.find((a) => a.featured);
  const rest = filtered.filter((a) => a !== featured);

  return (
    <section id="feed" className="w-full bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1320px] px-4 md:px-6">
        <SectionHeading
          eyebrow="热议前线"
          title="前排围观，热议现场直击"
          desc="不同栏目的最新热议与高光话题实时更新，点选栏目或使用搜索，快速锁定你想围观的第一现场。"
          action={
            <div className="flex flex-wrap gap-2">
              {feedCategories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onCategoryChange(c)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeCategory === c
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 hover:bg-secondary-100"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          }
        />

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-background-200 bg-background-50 p-12 text-center">
            <i className="ri-search-line mx-auto block text-4xl text-secondary-400"></i>
            <p className="mt-4 text-lg font-medium text-foreground-800">没有找到相关的话题</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词，或切回其他栏目再试试。</p>
            <button
              type="button"
              onClick={() => {
                onCategoryChange("全部");
              }}
              className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              查看全部热议
            </button>
          </div>
        ) : featured ? (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {bigCard(featured)}
            <div className="flex flex-col gap-5">{rest.slice(0, 4).map(smallCard)}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">{filtered.map(smallCard)}</div>
        )}
      </div>
    </section>
  );
}